import { useState } from "react"
import Layout from "@/components/Layout"
import DepreciationScheduleTable, { ScheduleRow } from "@/components/DepreciationScheduleTable"
import { exportToCSV } from "@/lib/exportCsv"
import {Truck} from 'lucide-react'

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
}

const EQUIPMENT_TYPES = [
  { label: "Farm Tractor (7-yr)", life: 7 },
  { label: "Semi-Truck / Tractor-Trailer (5-yr)", life: 5 },
  { label: "Construction Equipment — Bulldozer, Excavator (7-yr)", life: 7 },
  { label: "Crane (7-yr)", life: 7 },
  { label: "Forklift (5-yr)", life: 5 },
  { label: "Agricultural Machinery (7-yr)", life: 7 },
  { label: "Land Improvements — Fences, Drainage (15-yr)", life: 15 },
]

export default function HeavyEquipmentCalculator() {
  const [cost, setCost] = useState("")
  const [salvage, setSalvage] = useState("")
  const [eqType, setEqType] = useState(0)
  const [method, setMethod] = useState("ddb")
  const [schedule, setSchedule] = useState<ScheduleRow[]>([])
  const [results, setResults] = useState<{ yr1: number; totalDepr: number } | null>(null)
  const [error, setError] = useState("")

  function calculate() {
    setError("")
    const c = parseFloat(cost)
    const s = parseFloat(salvage) || 0
    const { life } = EQUIPMENT_TYPES[eqType]
    if (isNaN(c) || c <= 0) { setError("Please enter a valid asset cost."); return }
    if (s >= c) { setError("Salvage value must be less than cost."); return }

    const rows: ScheduleRow[] = []
    let accumulated = 0

    if (method === "sl") {
      const annual = (c - s) / life
      for (let i = 1; i <= life; i++) {
        const begin = i === 1 ? c : c - accumulated
        accumulated += annual
        rows.push({ year: i, beginningValue: begin, depreciation: annual, accumulatedDepreciation: accumulated, endingBookValue: Math.max(s, c - accumulated) })
      }
    } else {
      const rate = 2 / life
      let bv = c
      for (let i = 1; i <= life; i++) {
        const begin = bv
        let depr = bv * rate
        const slD = (bv - s) / (life - i + 1)
        if (slD > depr) depr = slD
        if (bv - depr < s) depr = bv - s
        if (depr < 0) depr = 0
        accumulated += depr
        bv -= depr
        rows.push({ year: i, beginningValue: begin, depreciation: depr, accumulatedDepreciation: accumulated, endingBookValue: bv })
        if (bv <= s) break
      }
    }

    setSchedule(rows)
    setResults({ yr1: rows[0]?.depreciation ?? 0, totalDepr: c - s })
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-amber-600 rounded-lg p-2">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-amber-600 uppercase tracking-wide">Asset Specific</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Tractor, Trailer & Heavy Equipment Depreciation Calculator
          </h1>
          <p className="text-lg text-gray-600">
            Calculate depreciation for tractors, semi-trucks, construction equipment, cranes,
            and agricultural machinery using IRS MACRS guidelines.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Enter Equipment Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Equipment Type</label>
              <select value={eqType} onChange={(e) => setEqType(parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 text-gray-900">
                {EQUIPMENT_TYPES.map((e, i) => <option key={e.label} value={i}>{e.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Cost ($)</label>
              <input type="number" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="e.g. 150000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salvage Value ($)</label>
              <input type="number" value={salvage} onChange={(e) => setSalvage(e.target.value)} placeholder="e.g. 15000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Depreciation Method</label>
            <div className="flex gap-4">
              {[["ddb", "Double Declining Balance (Accelerated)"], ["sl", "Straight Line"]].map(([v, l]) => (
                <label key={v} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" value={v} checked={method === v} onChange={() => setMethod(v)} className="text-amber-600" />
                  <span className="text-sm text-gray-700">{l}</span>
                </label>
              ))}
            </div>
          </div>
          {error ? <p className="text-red-600 text-sm mb-4">{error}</p> : null}
          <button onClick={calculate}
            className="bg-amber-600 hover:bg-amber-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors">
            Calculate Equipment Depreciation
          </button>

          {results !== null ? (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-amber-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-amber-700">{fmt(results.yr1)}</div>
                <div className="text-sm text-gray-600 mt-1">Year 1 Deduction</div>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-blue-700">{EQUIPMENT_TYPES[eqType].life} yrs</div>
                <div className="text-sm text-gray-600 mt-1">MACRS Recovery Period</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-gray-700">{fmt(results.totalDepr)}</div>
                <div className="text-sm text-gray-600 mt-1">Total Depreciable Amount</div>
              </div>
            </div>
          ) : null}
          {schedule.length > 0 ? <DepreciationScheduleTable rows={schedule} onExport={() => exportToCSV(schedule, "heavy-equipment-depreciation-schedule.csv")} /> : null}
        </div>

        <div className="prose max-w-none">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Heavy Equipment Depreciation — IRS Guidelines</h2>
          <p className="text-gray-700 mb-6">
            Agricultural and construction equipment generally falls into the <strong>7-year MACRS
            property class</strong>, while trucks, trailers, and cargo containers are typically
            <strong> 5-year property</strong>. Using accelerated depreciation maximizes tax
            deductions in the years when equipment experiences the most use.
          </p>
          <div className="overflow-x-auto rounded-xl border border-gray-200 mb-6">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Equipment Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">MACRS Class</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Typical Life</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Notes</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Farm Tractor", "7-Year", "7 yrs", "Qualifies for Section 179"],
                  ["Semi-Truck / Tractor", "5-Year", "5 yrs", "Qualifies for Section 179"],
                  ["Trailer / Cargo Container", "5-Year", "5 yrs", "Attached to truck"],
                  ["Bulldozer / Excavator", "7-Year", "7 yrs", "Construction equipment"],
                  ["Crane", "7-Year", "7 yrs", "Heavy lift equipment"],
                  ["Forklift", "5-Year", "5 yrs", "Warehouse equipment"],
                  ["Land Improvements", "15-Year", "15 yrs", "Fences, drainage, roads"],
                ].map(([eq, cls, life, notes], i) => (
                  <tr key={eq} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="px-4 py-3 text-gray-700 font-medium">{eq}</td>
                    <td className="px-4 py-3 text-amber-700 font-medium">{cls}</td>
                    <td className="px-4 py-3 text-gray-600">{life}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <h3 className="font-semibold text-amber-900 mb-1">Bonus Depreciation for Heavy Equipment</h3>
            <p className="text-amber-800 text-sm">
              New and used heavy equipment placed in service after September 27, 2017 may qualify for
              <strong> 100% bonus depreciation</strong> (phasing down to 60% in 2024, 40% in 2025).
              This allows an immediate first-year deduction of the entire purchase price.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  )
}
